import React, { useState, useEffect } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { LogBadge, Spinner } from '../components/UI';

const fmtDate = (d) => new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function DashboardPage() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/passwords'),
      api.get('/groups'),
      isAdmin ? api.get('/users') : Promise.resolve({ data: [] }),
      isAdmin ? api.get('/logs?limit=15') : Promise.resolve({ data: { logs: [] } }),
    ]).then(([pw, gr, us, lg]) => {
      setData({
        passwords: pw.data,
        groups: gr.data,
        users: us.data,
        logs: lg.data.logs || [],
      });
    }).catch(() => {});
  }, [isAdmin]);

  if (!data) return <><div className="topbar"><h2>Dashboard</h2></div><div className="page"><Spinner /></div></>;

  const today = new Date().toDateString();
  const todayAct = data.logs.filter(l => new Date(l.created_at).toDateString() === today).length;

  return (
    <>
      <div className="topbar"><h2>Dashboard</h2></div>
      <div className="page">
        {/* Stats */}
        <div className="stats-grid">
          <StatCard label="Passwords" value={data.passwords.length} sub="entries stored" color="var(--accent)" />
          <StatCard label="Groups"    value={data.groups.length}    sub="access groups"  color="var(--green)" />
          {isAdmin && <StatCard label="Users"  value={data.users.length}     sub="registered"     color="var(--purple)" />}
          <StatCard label="Today"    value={todayAct}               sub="actions today"  color="var(--amber)" />
        </div>

        {/* Group overview */}
        {data.groups.length > 0 && (
          <>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Group Overview</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 26 }}>
              {data.groups.map(g => (
                <div key={g.id} style={{
                  background: 'var(--s1)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r)', padding: '12px 16px', minWidth: 160,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: g.color, display: 'inline-block' }} />
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{g.name}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--sub)' }}>
                    🔑 {data.passwords.filter(p => p.group_id === g.id).length} passwords
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--sub)' }}>
                    👥 {Array.isArray(g.members) ? g.members.filter(m => m?.id).length : 0} members
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Recent activity */}
        {isAdmin && (
          <>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Recent Activity</div>
            <div className="tbl-wrap">
              <table>
                <thead><tr><th>Time</th><th>User</th><th>Action</th><th>Detail</th></tr></thead>
                <tbody>
                  {data.logs.length === 0 && (
                    <tr><td colSpan={4}><div className="empty-row"><div className="ei">📋</div>No activity yet.</div></td></tr>
                  )}
                  {data.logs.map(l => (
                    <tr key={l.id}>
                      <td className="mono muted small" style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.created_at)}</td>
                      <td style={{ fontWeight: 600 }}>{l.username}</td>
                      <td><LogBadge action={l.action} /></td>
                      <td style={{ fontSize: 13, color: 'var(--sub)' }}>{l.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="stat">
      <div className="s-lbl">{label}</div>
      <div className="s-num" style={{ color }}>{value}</div>
      <div className="s-sub">{sub}</div>
    </div>
  );
}
